<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <div class="title-head">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('payroll.payslips') }} - {{ payrollRun.name }}
          </oxd-text>
          <oxd-text
            v-if="payrollRun.status"
            tag="p"
            class="orangehrm-sub-title"
          >
            {{ $t('general.status') }}:
            <span class="payroll-run-status">{{ payrollRun.status }}</span>
          </oxd-text>
          <oxd-text
            v-if="processingBanner"
            tag="p"
            class="orangehrm-sub-title payroll-processing-banner"
            role="status"
          >
            {{ processingBanner }}
          </oxd-text>
        </div>
        <div v-if="headerActions.length" class="orangehrm-header-actions">
          <div
            v-for="action in headerActions"
            :key="action.key"
            class="payroll-run-action"
            :title="action.description"
          >
            <oxd-button
              :label="action.label"
              :icon-name="action.icon"
              display-type="secondary"
              @click="action.onClick"
            />
            <span class="payroll-run-action__tooltip" role="tooltip">
              {{ action.description }}
            </span>
          </div>
        </div>
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="headers"
          :items="items?.data"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div
        v-if="collectiveErrorMessages.length > 0"
        class="payroll-error-list-container"
      >
        <div class="payroll-collective-warn" role="alert">
          <oxd-icon
            name="exclamation-triangle"
            class="payroll-collective-warn__icon"
          />
          <ul class="payroll-collective-warn__list">
            <li
              v-for="(message, index) in collectiveErrorMessages"
              :key="`${index}-${message}`"
              class="payroll-collective-warn__item"
            >
              {{ message }}
            </li>
          </ul>
        </div>
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
      <div class="orangehrm-button-row">
        <oxd-button
          display-type="ghost"
          :label="$t('general.back')"
          @click="onCancel"
        />
      </div>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
    <delete-confirmation ref="runDeleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {APIService} from '@ohrm/core/util/services/api.service';
import {OxdIcon} from '@ohrm/oxd';
import moment from 'moment';
import {ref} from 'vue';
import {navigate} from '@/core/util/helper/navigation';
import {openAuthenticatedPayslip} from '@/orangehrmPayrollPlugin/util/openAuthenticatedPayslip';

const RUN_ACTION_TOOLTIPS = {
  run_action_edit_tooltip:
    'Open payroll run settings (period, employees, pay date, and template) before processing.',
  run_action_delete_tooltip:
    'Permanently remove this draft payroll run. Only available while status is draft.',
  run_action_process_tooltip:
    'Calculate payslips for all selected employees and generate payroll totals and CSV export.',
  run_action_retry_tooltip:
    'Set the run back to draft so you can adjust settings and process again.',
  run_action_reset_tooltip:
    'Delete all payslips, clear totals, and return this run to draft for reprocessing.',
  run_action_finalize_tooltip:
    'Lock payslips and mark the run as finalized so payslips can be emailed to employees.',
  run_action_view_email_batch_tooltip:
    'Open bulk email sending for finalized payslips in this run.',
  run_action_view_csv_tooltip:
    'Download or open the payroll CSV export generated during processing.',
};

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'oxd-icon': OxdIcon,
  },
  props: {
    payrollRunId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const payrollRun = ref({
      name: '',
      status: '',
      csvUrl: '',
      totalEmployees: 0,
      configuration: null,
    });
    const processPollTimer = ref(null);
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/payslips',
    );

    const payrollRunApi = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/payroll/runs/${props.payrollRunId}`,
    );

    const payrollRunsListApi = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/runs',
    );

    const loadPayrollRun = async () => {
      const response = await payrollRunApi.get();
      const {data} = response.data;
      payrollRun.value = {
        name: data.name,
        status: data.status,
        csvUrl: data?.csv?.downloadUrl?.replace('/web', '') || '',
        totalEmployees: data.totalEmployees ?? 0,
        configuration: data.configuration ?? null,
      };
      return data;
    };

    const formatTime = (time) => {
      if (!time) return '';
      const utcTime = moment.utc(time, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
      const localTime = utcTime.clone().tz(moment.tz.guess());
      return localTime.format('YYYY-MM-DD HH:mm:ss');
    };

    const formatAmount = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return '';
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    };

    const formatDays = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return '';
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    };

    const resolveWorkdays = (attendanceSummary) => {
      if (!attendanceSummary || typeof attendanceSummary !== 'object') {
        return null;
      }
      // WF / attendance fraction — excludes paid leave and holidays.
      return Number(attendanceSummary.attendanceDays) || 0;
    };

    const dataNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          status: item.status,
          employee: item.employee.fullName,
          workdays: formatDays(resolveWorkdays(item.attendanceSummary)),
          totalEarnings: formatAmount(item.grossAmount),
          totalDeductions: formatAmount(item.totalDeductions),
          netPay: formatAmount(item.netAmount),
          generatedAt: formatTime(item.generatedAt?.date),
          emailSentAt: formatTime(item.emailSentAt?.date),
          emailStatus: item.emailStatus ?? 'never',
          downloadUrl: item.downloadUrl.replace('/web', ''),
          notes: item.notes ?? null,
          slipSize: item.slipSize ?? 0,
        };
      });
    };

    const more = {
      component: 'oxd-table-dropdown',
      props: {
        options: [],
        style: {'margin-left': 'auto'},
        onClick: null,
      },
    };

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      prefetch: false,
      normalizer: dataNormalizer,
      query: {payrollRunId: props.payrollRunId},
    });

    execQuery(false);

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      payrollRun,
      payrollRunApi,
      payrollRunsListApi,
      loadPayrollRun,
      processPollTimer,
      more,
    };
  },
  data() {
    return {
      headers: [
        {
          name: 'employee',
          title: this.columnTitle('general.employee_name', 'Employee'),
          slot: 'title',
          style: {flex: 1.2, 'text-transform': 'capitalize'},
        },
        {
          name: 'status',
          title: this.columnTitle('general.status', 'Status'),
          style: {flex: 0.7, 'text-transform': 'capitalize'},
        },
        {
          name: 'workdays',
          title: this.columnTitle('payroll.workdays', 'Workdays'),
          style: {
            flex: 0.8,
            'justify-content': 'flex-end',
            'text-align': 'right',
          },
        },
        {
          name: 'totalEarnings',
          title: this.columnTitle('payroll.total_earnings', 'Total Earnings'),
          style: {
            flex: 1,
            'justify-content': 'flex-end',
            'text-align': 'right',
          },
        },
        {
          name: 'totalDeductions',
          title: this.columnTitle(
            'payroll.total_deductions',
            'Total Deductions',
          ),
          style: {
            flex: 1,
            'justify-content': 'flex-end',
            'text-align': 'right',
          },
        },
        {
          name: 'netPay',
          title: this.columnTitle('payroll.net_pay', 'Net Pay'),
          style: {
            flex: 1,
            'justify-content': 'flex-end',
            'text-align': 'right',
          },
        },
        {
          name: 'generatedAt',
          title: this.columnTitle('payroll.generated', 'Generated'),
          style: {flex: 1},
        },
        {
          name: 'emailSentAt',
          title: this.columnTitle('payroll.email_sent', 'Email Sent'),
          style: {flex: 1},
        },
        {
          name: 'payslipIssue',
          title: '',
          style: {
            flex: '0 0 2rem',
            'justify-content': 'center',
            'align-items': 'center',
          },
          cellRenderer: this.payslipWarnCellRenderer,
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.columnTitle('general.actions', 'Actions'),
          style: {flex: 0.9, 'text-align': 'center'},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ],
      checkedItems: [],
      isProcessingPoll: false,
      payslipEmailPollTimers: {},
    };
  },
  computed: {
    processingBanner() {
      if ((this.payrollRun.status || '').toLowerCase() !== 'processing') {
        return null;
      }
      const total = this.payrollRun.totalEmployees || 0;
      const done = this.total || 0;
      const label = this.$t('payroll.processing_payroll_run');
      return total > 0 ? `${label} (${done} / ${total})` : label;
    },
    collectiveErrorMessages() {
      const rows = this.items?.data ?? [];
      const errors = rows
        .map((row) => row?.notes)
        .filter((note) => typeof note === 'string' && note.trim() !== '');
      return [...new Set(errors)];
    },
    headerActions() {
      const status = (this.payrollRun.status || '').toLowerCase();
      const actions = [];

      if (status === 'draft') {
        actions.push(
          this.buildRunAction(
            'edit',
            this.$t('general.edit'),
            'pencil-fill',
            this.onClickEditRun,
            'run_action_edit_tooltip',
          ),
        );
        actions.push(
          this.buildRunAction(
            'delete',
            this.$t('general.delete'),
            'trash',
            this.onClickDeleteRun,
            'run_action_delete_tooltip',
          ),
        );
        actions.push(
          this.buildRunAction(
            'process',
            this.$t('payroll.process'),
            'play-fill',
            this.onClickProcess,
            'run_action_process_tooltip',
          ),
        );
      }

      if (status === 'processing') {
        actions.push(
          this.buildRunAction(
            'reset',
            this.$t('payroll.reset'),
            'arrow-counterclockwise',
            () => this.onRunAction('reset'),
            'run_action_reset_tooltip',
          ),
        );
      }

      if (status === 'cancelled' || status === 'failed') {
        actions.push(
          this.buildRunAction(
            'retry',
            this.$t('payroll.retry'),
            'arrow-clockwise',
            () => this.onRunAction('retry'),
            'run_action_retry_tooltip',
          ),
        );
      }

      if (status === 'finalized' || status === 'completed') {
        actions.push(
          this.buildRunAction(
            'reset',
            this.$t('payroll.reset'),
            'arrow-counterclockwise',
            () => this.onRunAction('reset'),
            'run_action_reset_tooltip',
          ),
        );
      }

      if (status === 'completed') {
        actions.push(
          this.buildRunAction(
            'finalize',
            this.$t('payroll.finalize'),
            'check-lg',
            () => this.onRunAction('finalize'),
            'run_action_finalize_tooltip',
          ),
        );
      }

      if (status === 'finalized') {
        actions.push(
          this.buildRunAction(
            'view_email_batch',
            this.$t('payroll.view_email_batch'),
            'envelope',
            this.onClickViewEmailBatch,
            'run_action_view_email_batch_tooltip',
          ),
        );
      }

      if (this.payrollRun.csvUrl && status !== 'draft') {
        actions.push(
          this.buildRunAction(
            'view_csv',
            this.$t('payroll.view_csv'),
            'file-earmark-spreadsheet',
            this.onClickViewCsv,
            'run_action_view_csv_tooltip',
          ),
        );
      }

      return actions;
    },
  },
  beforeUnmount() {
    this.stopProcessingPoll();
    Object.keys(this.payslipEmailPollTimers).forEach((payslipId) => {
      this.stopPayslipEmailPoll(payslipId);
    });
  },
  mounted() {
    this.loadPayrollRun().then(() => {
      if ((this.payrollRun.status || '').toLowerCase() === 'processing') {
        this.startProcessingPoll();
      }
    });
  },

  methods: {
    columnTitle(i18nKey, fallback) {
      const translated = this.$t(i18nKey);
      return translated === i18nKey ? fallback : translated;
    },
    buildRunAction(key, label, icon, onClick, tooltipUnitId) {
      const i18nKey = `payroll.${tooltipUnitId}`;
      const translated = this.$t(i18nKey);

      return {
        key,
        label,
        icon,
        onClick,
        description:
          translated === i18nKey
            ? RUN_ACTION_TOOLTIPS[tooltipUnitId] ?? translated
            : translated,
      };
    },
    canViewPayslipSlip(row) {
      const notes = row?.notes;
      if (typeof notes === 'string' && notes.trim() !== '') {
        return false;
      }
      return Number(row?.slipSize ?? 0) > 0;
    },
    payslipUnavailableTitle(row) {
      const notes = row?.notes;
      if (typeof notes === 'string' && notes.trim() !== '') {
        return notes.trim();
      }
      return this.$t('general.warning');
    },
    payslipWarnCellRenderer(...[, , , row]) {
      if (this.canViewPayslipSlip(row)) {
        return {component: 'span'};
      }
      return {
        component: OxdIcon,
        props: {
          name: 'exclamation-triangle',
          title: this.payslipUnavailableTitle(row),
          class: 'payroll-payslip-warn-icon',
        },
      };
    },
    actionRenderer(...[, , , row]) {
      const dropdownActions = [];
      const cellConfig = {};
      if (
        !row.emailSentAt &&
        this.payrollRun.status.toLowerCase() === 'finalized'
      ) {
        dropdownActions.push({
          label: this.$t('payroll.send_email'),
          context: 'send_email',
        });
      }
      if (
        row.emailSentAt &&
        this.payrollRun.status.toLowerCase() === 'finalized'
      ) {
        dropdownActions.push({
          label: this.$t('payroll.resend_email'),
          context: 'resend_email',
        });
      }
      cellConfig.edit = {
        onClick: () => this.onClickEdit(row),
        props: {
          name: 'pencil-fill',
        },
      };
      if (this.canViewPayslipSlip(row)) {
        cellConfig.view = {
          onClick: () => this.onClickView(row),
          props: {
            name: 'file-earmark-text-fill',
          },
        };
      }
      if (dropdownActions.length > 0) {
        this.more.props.options = dropdownActions;
        this.more.props.onClick = ($event) =>
          this.onDropdownAction($event, row);
        cellConfig.more = this.more;
      }
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onDropdownAction(event, item) {
      if (event.context === 'send_email' || event.context === 'resend_email') {
        this.onSendEmail(item);
      }
    },
    onSendEmail(item) {
      const emailApi = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/payslips/${item.id}/send-email`,
      );
      this.isLoading = true;
      emailApi
        .create({})
        .then(() => {
          this.startPayslipEmailPoll(item.id);
        })
        .catch((response) => {
          return this.$toast.error({
            title: this.$t('general.error'),
            message: response?.response?.data?.error?.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startPayslipEmailPoll(payslipId) {
      this.stopPayslipEmailPoll(payslipId);
      this.payslipEmailPollTimers[payslipId] = setInterval(() => {
        this.pollPayslipEmailStatus(payslipId);
      }, 3000);
      this.pollPayslipEmailStatus(payslipId);
    },
    stopPayslipEmailPoll(payslipId) {
      const timer = this.payslipEmailPollTimers[payslipId];
      if (timer) {
        clearInterval(timer);
        delete this.payslipEmailPollTimers[payslipId];
      }
    },
    async pollPayslipEmailStatus(payslipId) {
      await this.execQuery(false);
      const row = (this.items?.data ?? []).find(
        (item) => item.id === payslipId,
      );
      if (!row) {
        return;
      }

      const status = (row.emailStatus || '').toLowerCase();
      if (status === 'sent') {
        this.stopPayslipEmailPoll(payslipId);
        await this.$toast.updateSuccess();
      } else if (status === 'failed' || status === 'aborted') {
        this.stopPayslipEmailPoll(payslipId);
        await this.$toast.error({
          title: this.$t('general.error'),
          message: this.$t('payroll.email_send_failed'),
        });
      }
    },
    onCancel() {
      navigate(`/payroll/viewPayrollRuns`);
    },
    onClickEditRun() {
      navigate('/payroll/editPayrollRuns/{id}', {id: this.payrollRunId});
    },
    onClickDeleteRun() {
      this.$refs.runDeleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deletePayrollRun();
        }
      });
    },
    deletePayrollRun() {
      this.isLoading = true;
      this.payrollRunsListApi
        .deleteAll({ids: [this.payrollRunId]})
        .then(() => {
          return this.$toast.deleteSuccess();
        })
        .then(() => {
          navigate('/payroll/viewPayrollRuns');
        })
        .catch((response) => {
          return this.$toast.error({
            title: this.$t('general.error'),
            message: response?.response?.data?.error?.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onRunAction(method) {
      const runActionApi = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/runs/${this.payrollRunId}/${method}`,
      );
      this.isLoading = true;
      runActionApi
        .create({})
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(async () => {
          await this.loadPayrollRun();
          await this.execQuery(false);
        })
        .catch((response) => {
          return this.$toast.error({
            title: this.$t('general.error'),
            message: response?.response?.data?.error?.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onClickProcess() {
      const runActionApi = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/runs/${this.payrollRunId}/process`,
      );
      this.isLoading = true;
      runActionApi
        .create({})
        .then(async () => {
          await this.loadPayrollRun();
          this.startProcessingPoll();
        })
        .catch((response) => {
          return this.$toast.error({
            title: this.$t('general.error'),
            message: response?.response?.data?.error?.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startProcessingPoll() {
      this.stopProcessingPoll();
      if ((this.payrollRun.status || '').toLowerCase() !== 'processing') {
        return;
      }
      this.isProcessingPoll = true;
      this.processPollTimer = setInterval(() => {
        this.pollProcessingStatus();
      }, 3000);
      this.pollProcessingStatus();
    },
    stopProcessingPoll() {
      if (this.processPollTimer) {
        clearInterval(this.processPollTimer);
        this.processPollTimer = null;
      }
      this.isProcessingPoll = false;
    },
    async pollProcessingStatus() {
      try {
        const prevStatus = (this.payrollRun.status || '').toLowerCase();
        const data = await this.loadPayrollRun();
        await this.execQuery(false);
        const status = (data?.status || '').toLowerCase();

        if (status === 'completed') {
          this.stopProcessingPoll();
          await this.$toast.updateSuccess();
        } else if (status === 'draft' && prevStatus === 'processing') {
          this.stopProcessingPoll();
          const stale = data?.configuration?.processing_stale_recovered_at;
          const message = stale
            ? this.$t('payroll.processing_timed_out')
            : data?.configuration?.processing_last_error ||
              this.$t('general.error');
          await this.$toast.error({
            title: this.$t('general.error'),
            message,
          });
        }
      } catch (response) {
        // Keep polling on transient errors.
      }
    },
    onClickViewCsv() {
      if (this.payrollRun.csvUrl) {
        navigate(this.payrollRun.csvUrl);
      }
    },
    onClickViewEmailBatch() {
      navigate(`/payroll/viewPayrollEmailBatches/${this.payrollRunId}`);
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickEdit(item) {
      navigate(`/payroll/editPayrollPayslips/${item.id}/${this.payrollRunId}`);
    },
    async onClickView(item) {
      try {
        await openAuthenticatedPayslip(item.downloadUrl);
      } catch {
        this.$toast.error({
          title: this.$t('general.error'),
        });
      }
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery(false);
    },
  },
};
</script>
<style scoped>
.orangehrm-header-container {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.orangehrm-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.payroll-run-action {
  position: relative;
  display: inline-flex;
}

.payroll-run-action__tooltip {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  z-index: 20;
  width: max-content;
  max-width: 16rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.35rem;
  background: var(--oxd-interface-gray-darken-2-color, #384455);
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%);
  transition: opacity 0.15s ease, visibility 0.15s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.payroll-run-action__tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: var(--oxd-interface-gray-darken-2-color, #384455);
}

.payroll-run-action:hover .payroll-run-action__tooltip,
.payroll-run-action:focus-within .payroll-run-action__tooltip {
  opacity: 1;
  visibility: visible;
}

.orangehrm-sub-title {
  margin-top: 0.25rem;
  color: var(--oxd-interface-gray-darken-1-color, #64728c);
  text-transform: capitalize;
}

.payroll-run-status {
  text-transform: capitalize;
}

.orangehrm-button-row {
  padding: 20px;
  text-align: left;
}

.payroll-error-list-container {
  margin-top: 1rem;
  padding: 1rem 1.25rem 1.25rem;
  font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
}

.payroll-collective-warn {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.65rem;
  border: 1px solid #f5c26b;
  background: #fff8e6;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #5d6574;
}

.payroll-collective-warn__icon {
  flex-shrink: 0;
  color: #e67e22;
  font-size: 1rem;
  margin-top: 0.05rem;
}

.payroll-collective-warn__list {
  margin: 0;
  padding-left: 1.1rem;
  flex: 1;
  font-size: inherit;
}

.payroll-collective-warn__item {
  margin: 0.2rem 0;
  line-height: 1.4;
  font-size: inherit;
}

:deep(.payroll-payslip-warn-icon) {
  color: #e67e22;
  font-size: 1rem;
  cursor: default;
  vertical-align: middle;
}
</style>
